import React from 'react';

export const NextIcon: React.FC = () => (
  <svg viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4">
    <path
      fillRule="evenodd"
      d="M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-4.293-4.293a1 1 0 010-1.414z"
      clipRule="evenodd"
    />
  </svg>
);

export const BackIcon: React.FC = () => (
  <svg viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4">
    <path
      fillRule="evenodd"
      d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z"
      clipRule="evenodd"
    />
  </svg>
);

interface StepShellProps {
  title: string;
  subtitle?: string;
  children: React.ReactNode;
  onBack?: () => void;
  onNext: () => void;
  onSkip?: () => void;
  saving?: boolean;
  error?: string;
  nextLabel?: string;
  /** When true, overlays the form fields so SAM can guide without the user accidentally editing. */
  disabled?: boolean;
}

/**
 * Shared card + footer chrome for guided-setup steps. Keeps every step body consistent
 * (white rounded card, title/subtitle, Back/Next, optional "Skip this step") so individual
 * step components only declare their own fields.
 */
export const StepShell: React.FC<StepShellProps> = ({
  title,
  subtitle,
  children,
  onBack,
  onNext,
  onSkip,
  saving = false,
  error,
  nextLabel = 'Next',
  disabled = false,
}) => (
  <div className="bg-ui-card rounded-2xl p-8 shadow-sm">
    <h2 className="text-xl font-bold text-ui-text mb-1">{title}</h2>
    {subtitle && <p className="text-ui-text2 text-sm mb-6">{subtitle}</p>}

    <div className={disabled ? 'pointer-events-none opacity-60 select-none' : ''}>
      {children}
    </div>

    {error && <p className="text-red-500 text-sm mt-4 mb-1">{error}</p>}

    <div className="flex gap-3 mt-6">
      {onBack && (
        <button
          type="button"
          onClick={onBack}
          disabled={disabled}
          className="flex-1 border border-ui-border text-ui-text py-2.5 rounded-xl text-sm font-medium hover:bg-ui-hover transition flex items-center justify-center gap-1 disabled:opacity-40 disabled:cursor-not-allowed"
        >
          <BackIcon />
          Back
        </button>
      )}
      <button
        type="button"
        onClick={onNext}
        disabled={saving || disabled}
        className="flex-1 bg-[#1c1849] text-white py-2.5 rounded-xl text-sm font-medium hover:bg-[#2d2b6b] transition disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-1.5"
      >
        {saving ? 'Saving...' : (
          <>
            {nextLabel}
            <NextIcon />
          </>
        )}
      </button>
    </div>

    {onSkip && (
      <button
        type="button"
        onClick={onSkip}
        disabled={disabled}
        className="w-full mt-3 text-sm text-ui-text3 hover:text-ui-text2 transition disabled:opacity-40 disabled:cursor-not-allowed"
      >
        Skip this step
      </button>
    )}
  </div>
);
